import { useMemo, useState, type FormEvent } from 'react'
import type { MigrationCtx } from '@/lib/migration/useMigration'
import { useMigrationStore } from '@/stores/migrationStore'
import { unassignedFindings } from '@/lib/migration/summary'
import type { DiscoveryFinding, MigrationItem } from '@/api/types/migration'
import { RdModal, RdField } from '@/components/readiness/RdModal'
import { Chip, Icon } from '@/components/readiness/primitives'
import { formatDate } from '@/components/common/format'
import { CAPABILITIES, CAPABILITY_LABEL, type Capability } from '@/api/types/catalogue'
import { useCatalogueStore } from '@/stores/catalogueStore'
import { matchTechnology } from '@/lib/catalogue/engine'

// Default capability an item changes, from the asset type (editable).
const CAP_FOR_TYPE: Record<string, Capability> = { hsm: 'signing', library: 'tls_termination', network: 'ipsec_vpn', middleware: 'tls_termination', database: 'data_at_rest', service: 'ssh', certificate: 'certificate_issuance', saas: 'signing', token: 'signing', embedded: 'signing', cloud: 'tls_termination' }

// Create a migration item (§2.1): from a Discovery finding (algorithm, scope
// and system pre-filled, the finding marked assigned), from a CBOM asset group
// (M3 F01), or blank.

export function ItemModal({ ctx, finding, onClose, onCreated }: { ctx: MigrationCtx; finding?: DiscoveryFinding | null; onClose: () => void; onCreated: (item: MigrationItem) => void }) {
  const { orgId, indexId, actor, mscope } = ctx
  const createItem = useMigrationStore((s) => s.createItem)
  const facts = mscope?.facts
  const [mode, setMode] = useState<'finding' | 'group' | 'blank'>(finding ? 'finding' : 'blank')
  const [findingId, setFindingId] = useState<string>(finding?.id ?? '')
  const [groupId, setGroupId] = useState('')
  const [title, setTitle] = useState('')
  const [systemId, setSystemId] = useState(finding?.system_id ?? '')
  const [current, setCurrent] = useState(finding?.algorithm ?? '')
  const [target, setTarget] = useState('')
  const [owner, setOwner] = useState('')
  const [date, setDate] = useState('')
  const [waveId, setWaveId] = useState('')
  const [assetIds, setAssetIds] = useState<string[]>(finding?.asset_ids ?? [])
  // Iteration 3 §8: an item belongs to exactly one component; findings pre-fill it.
  const componentOfAssets = (ids: string[]) => facts?.assets.find((a) => ids.includes(a.id))?.component_id ?? ''
  const capabilityOfAssets = (ids: string[]): Capability | '' => { const a = facts?.assets.find((x) => ids.includes(x.id)); return a ? CAP_FOR_TYPE[a.asset_type] ?? '' : '' }
  const [componentId, setComponentId] = useState(finding ? componentOfAssets(finding.asset_ids) : '')
  const [capability, setCapability] = useState<Capability | ''>(finding ? capabilityOfAssets(finding.asset_ids) : '')
  const [follows, setFollows] = useState(false)
  const techs = useCatalogueStore((s) => s.technologies)
  const caps = useCatalogueStore((s) => s.capabilities)
  // The vendor roadmap month for the scoped assets' technology and capability.
  const catalogueDate = useMemo(() => {
    if (!capability) return null
    const months = assetIds.map((id) => facts?.assets.find((a) => a.id === id)).filter(Boolean).map((a) => {
      const t = matchTechnology(a!, techs)
      return t ? caps.find((c) => c.technology_id === t.id && c.capability === capability)?.roadmap_date ?? null : null
    }).filter((x): x is string => !!x).sort()
    if (!months.length) return null
    const [y, m] = months[months.length - 1].split('-').map(Number)
    return new Date(Date.UTC(y, m, 0)).toISOString().slice(0, 10)
  }, [assetIds, capability, facts, techs, caps])

  const unassigned = useMemo(() => (mscope ? unassignedFindings(mscope) : []), [mscope])
  const groups = useMemo(() => {
    const m = new Map<string, { id: string; name: string; system_id: string; algorithm: string; asset_ids: string[] }>()
    facts?.assets.forEach((a) => {
      if (!a.group_id) return
      const g = m.get(a.group_id) ?? { id: a.group_id, name: a.group_name ?? a.group_id, system_id: a.system_id, algorithm: a.current_algorithm, asset_ids: [] }
      g.asset_ids.push(a.id)
      m.set(a.group_id, g)
    })
    return Array.from(m.values())
  }, [facts])

  const pickFinding = (id: string) => {
    setFindingId(id)
    const f = unassigned.find((x) => x.id === id)
    if (!f) return
    setSystemId(f.system_id)
    setCurrent(f.algorithm)
    setAssetIds(f.asset_ids)
    setComponentId(componentOfAssets(f.asset_ids))
    setCapability(capabilityOfAssets(f.asset_ids))
    if (!title) setTitle(f.title)
  }
  const pickGroup = (id: string) => {
    setGroupId(id)
    const g = groups.find((x) => x.id === id)
    if (!g) return
    setSystemId(g.system_id)
    setCurrent(g.algorithm)
    setAssetIds(g.asset_ids)
    setComponentId(componentOfAssets(g.asset_ids))
    setCapability(capabilityOfAssets(g.asset_ids))
    if (!title) setTitle(`${g.name} · migration`)
  }
  const components = (facts?.components ?? []).filter((c) => c.system_id === systemId)
  const systemAssets = (facts?.assets ?? []).filter((a) => a.system_id === systemId && a.quantum_vulnerable && (!componentId || a.component_id === componentId))
  const needsComponent = !!systemId && components.length > 0 && !componentId

  const submit = (e: FormEvent) => {
    e.preventDefault()
    if (!title.trim() || needsComponent) return
    const item = createItem(orgId, indexId, {
      title: title.trim(),
      system_id: systemId || null,
      component_id: componentId || null,
      capability: capability || null,
      target_source: follows ? 'catalogue' : 'owner',
      current_algorithm: current.trim(),
      target_algorithm: target.trim(),
      owner: owner.trim() || null,
      target_date: follows ? catalogueDate : date || null,
      vendor_gated_year: follows && catalogueDate ? Number(catalogueDate.slice(0, 4)) : null,
      wave_id: waveId || null,
      asset_ids: assetIds,
      created_from_finding_id: mode === 'finding' ? findingId || null : null,
      created_from_group: mode === 'group' && !!groupId,
    }, actor)
    onCreated(item)
  }

  return (
    <RdModal
      title="New migration item"
      width={640}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="rd-btn" onClick={onClose}>Cancel</button>
          <button type="submit" form="rd-new-item" className="rd-btn rd-btn-primary" disabled={!title.trim() || needsComponent} data-testid="create-item"><Icon name="plus" size={14} /> Create item</button>
        </>
      }
    >
      <form id="rd-new-item" onSubmit={submit}>
        <fieldset className="mb-3">
          <legend className="mb-1 block text-[12.5px] font-medium" style={{ color: 'var(--rd-text-secondary)' }}>Create from</legend>
          <div className="flex flex-wrap gap-2">
            {([['finding', `Discovery finding · ${unassigned.length} unassigned`], ['group', `CBOM asset group · ${groups.length}`], ['blank', 'Blank']] as const).map(([k, label]) => (
              <label key={k} className="rd-btn cursor-pointer" style={mode === k ? { borderColor: 'var(--rd-accent-primary)', color: 'var(--rd-accent-link)' } : undefined}>
                <input type="radio" name="item-mode" className="sr-only" checked={mode === k} onChange={() => setMode(k)} />
                {label}
              </label>
            ))}
          </div>
        </fieldset>
        {mode === 'finding' && (
          <RdField label="Finding" htmlFor="ni-finding" hint="Findings with no migration item. The finding is marked assigned on create.">
            <select id="ni-finding" className="rd-input" value={findingId} onChange={(e) => pickFinding(e.target.value)} data-testid="finding-picker">
              <option value="">Choose a finding…</option>
              {unassigned.map((f) => <option key={f.id} value={f.id}>{f.title} · {facts?.systems.find((s) => s.id === f.system_id)?.name} · {formatDate(f.found_at)}</option>)}
            </select>
          </RdField>
        )}
        {mode === 'group' && (
          <RdField label="Asset group" htmlFor="ni-group">
            <select id="ni-group" className="rd-input" value={groupId} onChange={(e) => pickGroup(e.target.value)}>
              <option value="">Choose a group…</option>
              {groups.map((g) => <option key={g.id} value={g.id}>{g.name} · {g.asset_ids.length} assets</option>)}
            </select>
          </RdField>
        )}
        <RdField label="Title" htmlFor="ni-title" required>
          <input id="ni-title" className="rd-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Hybrid ML-KEM TLS on payments edge" />
        </RdField>
        <div className="grid gap-3 sm:grid-cols-2">
          <RdField label="System" htmlFor="ni-system">
            <select id="ni-system" className="rd-input" value={systemId} onChange={(e) => { setSystemId(e.target.value); setComponentId(''); setAssetIds([]) }}>
              <option value="">—</option>
              {(facts?.systems ?? []).map((s) => <option key={s.id} value={s.id}>{s.name}{s.cii ? ' · CII' : ''}</option>)}
            </select>
          </RdField>
          <RdField label="Component" htmlFor="ni-component" hint={systemId && !components.length ? 'Discovery reports no components for this system' : 'Detected by Discovery; an item belongs to one component'} required={components.length > 0}>
            <select id="ni-component" className="rd-input" value={componentId} onChange={(e) => { setComponentId(e.target.value); setAssetIds([]) }} disabled={!systemId || !components.length} data-testid="component-picker">
              <option value="">{systemId ? 'Choose a component…' : 'Choose a system first'}</option>
              {components.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </RdField>
          <RdField label="Capability" htmlFor="ni-cap" hint="The cryptographic function the item changes">
            <select id="ni-cap" className="rd-input" value={capability} onChange={(e) => setCapability(e.target.value as Capability | '')}>
              <option value="">—</option>
              {CAPABILITIES.map((c) => <option key={c} value={c}>{CAPABILITY_LABEL[c]}</option>)}
            </select>
          </RdField>
          <RdField label="Current algorithm" htmlFor="ni-current">
            <input id="ni-current" className="rd-input rd-mono" value={current} onChange={(e) => setCurrent(e.target.value)} placeholder="RSA-2048" />
          </RdField>
          <RdField label="Target algorithm" htmlFor="ni-target">
            <input id="ni-target" className="rd-input rd-mono" value={target} onChange={(e) => setTarget(e.target.value)} placeholder="ML-KEM-768 hybrid" />
          </RdField>
          <RdField label="Owner" htmlFor="ni-owner">
            <input id="ni-owner" className="rd-input" value={owner} onChange={(e) => setOwner(e.target.value)} />
          </RdField>
          <RdField label="Target date" htmlFor="ni-date">
            <input id="ni-date" type="date" className="rd-input" value={date} onChange={(e) => setDate(e.target.value)} disabled={follows} />
            <label className="mt-1 flex items-center gap-1.5 text-[11.5px]" style={{ color: 'var(--rd-text-muted)' }}><input type="checkbox" checked={follows} onChange={(e) => setFollows(e.target.checked)} /> Follow the catalogue vendor roadmap date{follows ? ` · ${catalogueDate ?? 'no roadmap date'}` : ''}</label>
          </RdField>
          <RdField label="Wave" htmlFor="ni-wave" hint="No service serves a wave plan today, so this is usually just Unscheduled.">
            <select id="ni-wave" className="rd-input" value={waveId} onChange={(e) => setWaveId(e.target.value)}>
              <option value="">Unscheduled</option>
              {(facts?.waves ?? []).map((w) => <option key={w.id} value={w.id}>{w.label}</option>)}
            </select>
          </RdField>
        </div>
        {systemId && systemAssets.length > 0 && (
          <fieldset className="mt-1">
            <legend className="mb-1 text-[12.5px] font-medium" style={{ color: 'var(--rd-text-secondary)' }}>Scope · {assetIds.length} of {systemAssets.length} assets</legend>
            <ul className="max-h-40 space-y-1 overflow-y-auto text-[12.5px]">
              {systemAssets.map((a) => (
                <li key={a.id}>
                  <label className="flex items-center gap-2">
                    <input type="checkbox" checked={assetIds.includes(a.id)} onChange={(e) => setAssetIds(e.target.checked ? [...assetIds, a.id] : assetIds.filter((x) => x !== a.id))} />
                    <span className="rd-mono">{a.code}</span> <span className="min-w-0 flex-1 truncate">{a.model_version}</span> {a.group_id && <Chip tone="chip">GROUP</Chip>}
                  </label>
                </li>
              ))}
            </ul>
          </fieldset>
        )}
      </form>
    </RdModal>
  )
}
